/* Marquee Night hero: endlessly drifting photo rows you can drag, plus a lightbox. */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const TAP_SLOP = 6;

  class MeekReel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.mh__track');
      if (!this.track || this.track.children.length === 0) return;

      // Clone the cards once so the row can loop without a visible seam.
      [...this.track.children].forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.tabIndex = -1;
        clone.removeAttribute('data-shopify-editor-block');
        this.track.appendChild(clone);
      });

      this.baseSpeed = reducedMotion ? 0 : parseFloat(this.dataset.speed || '-0.3');
      this.speed = this.baseSpeed;
      this.targetSpeed = this.baseSpeed;
      this.x = 0;
      this.last = performance.now();
      this.visible = true;

      this.addEventListener('pointerdown', this.onDown);
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.targetSpeed = this.baseSpeed * 0.15; });
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse' && !this.drag) this.targetSpeed = this.baseSpeed; });

      new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; }).observe(this);
      this.frame = this.frame.bind(this);
      requestAnimationFrame(this.frame);
    }

    get loopWidth() {
      return this.track.scrollWidth / 2;
    }

    frame(now) {
      const dt = Math.min(now - this.last, 64);
      this.last = now;
      if (this.visible && !document.hidden) {
        if (!this.drag) {
          this.speed += (this.targetSpeed - this.speed) * 0.05;
          this.x += this.speed * dt * 0.06 * 1.6;
        }
        const w = this.loopWidth;
        if (w > 0) {
          if (this.x <= -w) this.x += w;
          if (this.x > 0) this.x -= w;
        }
        this.track.style.transform = `translate3d(${this.x}px,0,0)`;
      }
      requestAnimationFrame(this.frame);
    }

    onDown = (e) => {
      if (e.button !== 0) return;
      const card = e.target.closest('.mh__card');
      this.drag = { startX: e.clientX, startY: e.clientY, lastX: e.clientX, lastT: performance.now(), v: 0, moved: false, card, id: e.pointerId, locked: false };
      card?.classList.add('is-pressed');
      window.addEventListener('pointermove', this.onMove, { passive: false });
      window.addEventListener('pointerup', this.onUp);
      window.addEventListener('pointercancel', this.onUp);
    };

    onMove = (e) => {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.locked) {
        if (Math.abs(dx) < TAP_SLOP && Math.abs(dy) < TAP_SLOP) return;
        // Vertical intent: let the page scroll and drop the gesture.
        if (Math.abs(dy) > Math.abs(dx)) return this.onUp(e, true);
        d.locked = true;
        d.moved = true;
        d.card?.classList.remove('is-pressed');
        this.classList.add('is-dragging');
      }
      e.preventDefault();
      const now = performance.now();
      const step = e.clientX - d.lastX;
      this.x += step;
      d.v = step / Math.max(now - d.lastT, 1);
      d.lastX = e.clientX;
      d.lastT = now;
    };

    onUp = (e, cancelled = false) => {
      const d = this.drag;
      if (!d) return;
      window.removeEventListener('pointermove', this.onMove);
      window.removeEventListener('pointerup', this.onUp);
      window.removeEventListener('pointercancel', this.onUp);
      d.card?.classList.remove('is-pressed');
      this.classList.remove('is-dragging');
      this.drag = null;
      if (d.moved) {
        // Fling: carry the release velocity, then ease back to the drift.
        this.speed = Math.max(-40, Math.min(40, d.v * 16));
        this.targetSpeed = this.baseSpeed;
        return;
      }
      if (!cancelled && e.type === 'pointerup' && d.card) {
        this.dispatchEvent(new CustomEvent('reel:open', { bubbles: true, detail: { card: d.card } }));
      }
    };
  }

  if (!customElements.get('meek-reel')) customElements.define('meek-reel', MeekReel);

  const initLightbox = (root) => {
    const dialog = root.querySelector('.mh__lightbox');
    if (!dialog || dialog.dataset.ready) return;
    dialog.dataset.ready = '1';
    const img = dialog.querySelector('.mh__lb-img');
    const cap = dialog.querySelector('.mh__lb-cap');
    const originals = () => [...root.querySelectorAll('.mh__card:not([aria-hidden])')];
    let index = 0;
    let opener = null;

    const show = (i) => {
      const cards = originals();
      if (!cards.length) return;
      index = (i + cards.length) % cards.length;
      const card = cards[index];
      img.classList.remove('is-swapping');
      void img.offsetWidth;
      img.classList.add('is-swapping');
      img.src = card.dataset.full;
      img.alt = card.querySelector('img')?.alt || '';
      cap.textContent = card.dataset.name || '';
    };

    const open = (card) => {
      const cards = originals();
      const name = card.dataset.name;
      const i = cards.findIndex((c) => c.dataset.full === card.dataset.full && c.dataset.name === name);
      opener = card;
      show(i < 0 ? 0 : i);
      dialog.showModal();
    };

    root.addEventListener('reel:open', (e) => open(e.detail.card));
    root.addEventListener('keydown', (e) => {
      const card = e.target.closest?.('.mh__card');
      if (card && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(card); }
    });
    dialog.querySelector('.mh__lb-close').addEventListener('click', () => dialog.close());
    dialog.querySelector('.mh__lb-prev').addEventListener('click', () => show(index - 1));
    dialog.querySelector('.mh__lb-next').addEventListener('click', () => show(index + 1));
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    });
    dialog.addEventListener('close', () => opener?.focus({ preventScroll: true }));

    let sx = null;
    img.addEventListener('pointerdown', (e) => { sx = e.clientX; });
    img.addEventListener('pointerup', (e) => {
      if (sx === null) return;
      const dx = e.clientX - sx;
      sx = null;
      if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
    });
  };

  const boot = () => document.querySelectorAll('.mh').forEach(initLightbox);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
